<template>
  <div class="h-100 d-flex flex-column">
    <h3
      class="mb-4"
    >
      Creative Feedback
    </h3>
    <b-card
      class="flex-grow-1 p-0 h-100 overflow-hidden"
      no-body
    >
      <div
        class="h-100 overflow-y-auto"
      >
        <div
          v-if="isRequestingData"
          class="h-100 d-flex align-items-center justify-content-center"
        >
          <div
            class="loader"
          />
        </div>
        <b-card-body
          v-else
          class="p-4"
        >
          <p
            v-if="comments.length === 0"
            class="text-muted mb-0"
          >
            This content item has no comments
          </p>
          <b-list-group
            v-else
            class="p-0"
            flush
          >
            <b-list-group-item
              v-for="(comment, index) in comments"
              :key="index"
              :class="['p-0', {'mb-2' : index < comments.length}]"
              button
              @click="triggerAnnotation(comment)"
              @blur="resetTriggeredAnnotation()"
            >
              <comment-item
                :comment-item="comment"
              />
            </b-list-group-item>
          </b-list-group>
        </b-card-body>
      </div>
    </b-card>
    <b-input-group
      id="creative-comments-text-input"
      class="flex-shrink-0 mb-0"
    >
      <b-form-textarea
        id="creative-comments-text-input"
        placeholder="Creative Feedback"
        :value="newComment"
        @input="updateNewComment($event)"
        @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
        @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
        @keydown.enter="handleEnterKeyEvent($event)"
        rows="3"
        max-rows="3"
        no-resize
        :disabled="false"
        data-test="creative-comments-text-input"
      />
      <b-input-group-append>
        <b-button
          variant="primary"
          :class="{ 'btn-loading': isSendBtnLoading }"
          @click="sendFeedback()"
          :disabled="!canSendFeedback"
          data-test="creative-comment-submit-btn"
        >
          Send
        </b-button>
      </b-input-group-append>
    </b-input-group>
    <p class="mb-0 mt-2">
      Send (keyboard shortcut) <small><kbd class="tag-primary">CTRL + ENTER</kbd></small>
    </p>
  </div>
</template>
<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import _ from 'lodash';

import CommentItem from '@/modules/content/components/CreativesPage/Sidebar/CommentItem';

function initialData() {
  return {
    isRequestingData: false,
    isSendBtnLoading: false,
    comments: [],
    newComment: '',
  };
}

export default {
  name: 'Comments',
  components: {
    CommentItem,
  },
  props: {
    isSidebarOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return initialData();
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'selectedArticleUuid',
      'selectedCreativeId',
      'isTriggeredAnnotation',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_ENDED,
      campaignTypes.GET_HAS_CAMPAIGN_ACCESS,
      campaignTypes.GET_HAS_SHOWCASE_ACCESS,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CONTENT_ITEM_DISPLAY_PROPERTIES,
    ]),
    selectedCreativeArticleCombo() {
      return `${this.selectedCreativeId}-${this.selectedArticleUuid}`;
    },
    canSendFeedback() {
      return !this.GET_IS_CAMPAIGN_ENDED
        && this.GET_HAS_SHOWCASE_ACCESS
        && this.newComment !== ''
        && !this.isSendBtnLoading;
    },
  },
  methods: {
    ...mapMutations('content', [
      contentTypes.TOGGLE_ANNOTATIONS,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_CONTENT_COMMENTS,
      contentTypes.POST_CONTENT_COMMENT,
      contentTypes.REMOVE_CURRENT_ANNOTATION,
      contentTypes.TRIGGER_ANNOTATION,
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
      contentTypes.FETCH_CONTENT_COMMENTS_COUNT,
    ]),
    async fetchComments() {
      if (this.GET_HAS_CAMPAIGN_ACCESS || this.selectedArticleUuid) {
        this.isRequestingData = true;
        this.comments = await this.FETCH_CONTENT_COMMENTS({
          campaignId: this.campaign.id,
          creativeId: this.selectedCreativeId,
          contentId: this.selectedArticleUuid,
        });
        if (this.isSidebarOpen) this.TOGGLE_ANNOTATIONS(true);
        this.isRequestingData = false;
      }
    },
    updateNewComment(comment) {
      this.newComment = comment;
    },
    async sendFeedback() {
      this.isSendBtnLoading = true;
      await this.POST_CONTENT_COMMENT({
        campaignId: this.campaign.id,
        body: {
          creativeId: this.selectedCreativeId,
          contentUuid: this.selectedArticleUuid,
          comment: this.newComment.trim(),
        },
      })
        .then(() => {
          this.REMOVE_CURRENT_ANNOTATION();
          this.newComment = '';
          this.fetchComments();
          this.FETCH_CONTENT_COMMENTS_COUNT({ campaignId: this.campaign.id, creativeId: this.selectedCreativeId });
        });
      this.isSendBtnLoading = false;
    },
    handleEnterKeyEvent(event) {
      if (event.type === 'keydown' && event.key !== 'Enter') return;
      if (event.shiftKey || event.altKey) {
        return event.preventDefault();
      }
      if (!event.ctrlKey) return;
      this.sendFeedback();
    },
    async resetTriggeredAnnotation() {
      return this.TRIGGER_ANNOTATION({ commentLocation: null });
    },
    async triggerAnnotation(comment) {
      if (_.isEqual(this.isTriggeredAnnotation, comment.commentLocation)) await this.resetTriggeredAnnotation();
      this.TRIGGER_ANNOTATION(comment);
    },
    resetData() {
      Object.assign(this.$data, initialData());
    },
  },
  mounted() {
    this.fetchComments();
  },
  watch: {
    selectedCreativeArticleCombo(newValue, oldValue) {
      if (newValue !== oldValue && this.isSidebarOpen) {
        this.resetData();
        this.fetchComments();
      }
    },
    isSidebarOpen(newValue, oldValue) {
      if (newValue !== oldValue && this.isSidebarOpen) {
        this.resetData();
        this.fetchComments();
      }
    },
  },
};
</script>
